import { AdequacyQueryShape } from "@app/projects/CountyProjectContainer";
import { FilterType, RegionSetStatusType } from "@app/projects/enumerators";
import { RegionQueryShape } from "@app/projects/network-assessment/RegionStatusFilter";
import { parseQuerystring } from "@app/shared/utility/parseQuerystring";
import { Feature, FeatureCollection, Geometry, GeometryCollection } from "geojson";
import { LngLat, LngLatBounds, LngLatBoundsLike, MapMouseEvent } from "mapbox-gl";
import { createContext, FC, RefObject, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { featureCollection, geometryCollection } from '@turf/helpers';
import { bbox } from "@turf/bbox";
import { FilterContext } from "@app/projects/network-assessment/maps/network-coverage-map/contexts/FilterContext";
import { MapRef } from "react-map-gl/mapbox";
import { GAP_DENSITY_MAP_STYLE } from "@app/projects/network-assessment/maps/gap-density-map/GapDensityMapsConstants";
import { RegionFilterItem } from "@app/projects/adequacySharedProjectContextProps";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ProjectService from "@app/projects/projectService";
import ProjectServiceAreaMap from "@app/projects/network-assessment/maps/gap-density-map/projectServiceAreaMap";

interface GapDensityMapDataContextProps {
  selectedCountyFeature: Feature<Geometry, ProjectServiceAreaMap>,
  countyMaps: FeatureCollection<Geometry, ProjectServiceAreaMap>,
  dataBoundingBox: LngLatBoundsLike | null;
  hoveredCountyMap: Feature<Geometry, ProjectServiceAreaMap>,
  isLoading: boolean,
  mapRef: RefObject<MapRef>;
  mouseCoordinates: LngLat,
  getBoundingBoxFeatureCollection: (f: FeatureCollection) => LngLatBounds;
  getBoundingBoxFeature: (f: Feature) => LngLatBounds;
  filters: RegionFilterItem[],
  countyMapFilters: string[],
  handleMouseMoveOnCounty: (e: MapMouseEvent) => void;
  handleMouseLeaveOnCounty: () => void;
  handleMouseClickOnCounty: (e: MapMouseEvent) => void;
  setCountyFilter: (filter: string) => void;
}

export const getBuckets = (max: number, numberOfBuckets = GAP_DENSITY_MAP_STYLE.NUMBER_OF_BUCKETS): number[][] => {
  const size = Math.round((max - 1) / numberOfBuckets);
  const result: number[][] = [];
  result.push([0, 0]);
  for (let i = 0; i < numberOfBuckets; i++) {
    if (size === 0 && i === 0) continue;
    const inf = i + i * size;
    const sup = inf + size < max ? inf + size : max;
    result.push([inf === 0 ? 1 : inf, sup]);
    if (inf >= max || sup >= max) break;
  }
  return result;
};

const GapDensityMapDataContext = createContext<Partial<GapDensityMapDataContextProps>>({});

const GapDensityMapDataContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const projectService = new ProjectService();

  const history = useHistory();
  const projectContext = useContext(CountyProjectContext);
  const filterContext = useContext(FilterContext);

  const [countyMaps, setCountyMaps] = useState<FeatureCollection<Geometry, ProjectServiceAreaMap>>(null);
  const [selectedCountyFeature, setSelectedCountyFeature] = useState<Feature<Geometry, ProjectServiceAreaMap>>(null);
  const [dataBoundingBox, setDataBoundingBox] = useState<LngLatBoundsLike | null>(null);
  const [hoveredCountySSA, setHoveredCountySSA] = useState<string>(null);
  const [countyMapFilters, setCountyMapFilters] = useState<string[]>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mouseCoordinates, setMouseCoordinates] = useState<LngLat>(null);

  const mapRef = useRef<MapRef>(null);

  const getBoundingBoxFeatureCollection = (f: FeatureCollection): LngLatBounds => {
    if (!(f as FeatureCollection).features || (f as FeatureCollection).features.length === 0) {
      return null;
    }
    else {
      const boundingBox = bbox(f);
      return new LngLatBounds([boundingBox[0], boundingBox[1], boundingBox[2], boundingBox[3]]);
    }

  }

  const getBoundingBoxFeature = (f: Feature): LngLatBounds => {
    if (!f) {
      return null;
    }
    else {
      const boundingBox = bbox(f);
      return new LngLatBounds([boundingBox[0], boundingBox[1], boundingBox[2], boundingBox[3]]);
    }

  }

  const stateFilter = useMemo<RegionFilterItem>(() => {
    return projectContext.filters.find(f => f.filterType === FilterType.State);
  }, [projectContext.filters])

  const countyFilter = useMemo<RegionFilterItem>(() => {
    return projectContext.filters.find(f => f.filterType === FilterType.County);
  }, [projectContext.filters])

  const specialtyFilter = useMemo<RegionFilterItem>(() => {
    return projectContext.filters.find(f => f.filterType === FilterType.Specialty);
  }, [projectContext.filters])

  const setStateFilter = (state: string) => {
    filterContext.replaceFilterUrl(FilterType.State, stateFilter?.filterValue, state);
  }

  const setCountyFilter = (countySSA: string) => {
    filterContext.replaceFilterUrl(FilterType.County, countyFilter?.filterValue, countySSA);
  }

  const regionAndAdequacyQuery = (): (RegionQueryShape & AdequacyQueryShape) => (parseQuerystring(history.location.search));

  const tryParseJSON = (json: string): FeatureCollection => {
    try {
      return JSON.parse(json) as FeatureCollection;
    } catch {
      return null;
    }
  }

  const convertToFeatureCollection = (serviceAreaMaps: ProjectServiceAreaMap[]): FeatureCollection<Geometry, ProjectServiceAreaMap> => {
    return featureCollection((serviceAreaMaps
      ? serviceAreaMaps
        .flatMap((county) =>
          tryParseJSON(county.geoJSON)
            ?.features.map((p) =>
              // We want everything except for the GeometryCollection as we know we are not going to receive a GeometryCollection.
              geometryCollection([p.geometry as Exclude<Geometry, GeometryCollection>],
                {
                  ...county,
                  numberOfSpecialtyCodeWithGaps: county.specialties.filter(s => s.isGap).length,
                  specialtyCodeWithGaps: county.specialties.filter(s => s.isGap).map(s => s.specialtyCode),
                }
              )))
      : []).filter(feature => !!feature))
  };

  const trueString = true.toString();
  const getFilteredProjectServiceAreaMapsByFilters = useMemo<FeatureCollection<Geometry, ProjectServiceAreaMap>>(() => {
    if (isLoading) return;

    const query = regionAndAdequacyQuery();
    const specialty = specialtyFilter;

    let filteredCountyMaps = countyMaps.features.filter(p => p.properties.status !== RegionSetStatusType.Opportunity);
    setCountyMapFilters(null);

    const countyStatusFilters =
      query.active === trueString ||
      query.approved === trueString ||
      query.opportunity === trueString ||
      query.pending === trueString;

    if (countyStatusFilters) {
      filteredCountyMaps = countyMaps.features.filter(p =>
        (query.active === trueString && p.properties.status === RegionSetStatusType.Active)
        || (query.approved === trueString && p.properties.status === RegionSetStatusType.Approved)
        || (query.pending === trueString && p.properties.status === RegionSetStatusType.Pending)
        || (query.opportunity === trueString && p.properties.status === RegionSetStatusType.Opportunity));
    }

    const gapExceptionFilter = query.gaps === trueString || query.exceptions === trueString;

    if (gapExceptionFilter) {
      filteredCountyMaps = filteredCountyMaps.filter(p =>
        (query.gaps === trueString && p.properties.specialties.find(s => s.isGap === true && (!!specialty ? s.specialtyCode === specialty.filterValue : true)))
        || (query.exceptions === trueString && p.properties.specialties.find(s => s.hasException === true && (!!specialty ? s.specialtyCode === specialty.filterValue : true)))
      );
    }

    setCountyMapFilters(filteredCountyMaps.map(map => map.properties.countySSA));

    return featureCollection(filteredCountyMaps);
  }, [countyMaps, projectContext.filters, history.location.search]);

  const zoomToFilterBounds = (): LngLatBounds => {
    const serviceAreaMaps = getFilteredProjectServiceAreaMapsByFilters;

    let filteredServiceArea = serviceAreaMaps;
    let selectedCounty: Feature<Geometry, ProjectServiceAreaMap> = null;

    if (!countyFilter && !!stateFilter) {
      filteredServiceArea = featureCollection(filteredServiceArea.features.filter(p => p.properties.state === stateFilter.filterValue));
    } else if (!!countyFilter) {
      filteredServiceArea = featureCollection(filteredServiceArea.features.filter(p => p.properties.countySSA === countyFilter.filterValue));
      selectedCounty = filteredServiceArea.features.length === 1 ? filteredServiceArea.features[0] : null;
    }

    setSelectedCountyFeature(selectedCounty);
    return getBoundingBoxFeatureCollection(filteredServiceArea);
  }

  const fitBoundsWithPadding = (bounds: LngLatBoundsLike): void => {
    if (!bounds || !mapRef.current) {
      return;
    }

    mapRef.current.fitBounds(bounds, {
      duration: GAP_DENSITY_MAP_STYLE.GAP_DENSITY_MAP_PAN_DURATION,
      padding: GAP_DENSITY_MAP_STYLE.GAP_DENSITY_MAP_BOUNDS_PADDING
    });
  }

  useEffect(() => {
    if (!!dataBoundingBox) {
      fitBoundsWithPadding(dataBoundingBox);
    }
  }, [dataBoundingBox]);

  useEffect(() => {
    if (isLoading) return;

    if (projectContext.filters?.length > 0) {
      setDataBoundingBox(zoomToFilterBounds());
      return;
    } else {
      setSelectedCountyFeature(null);
    }

    if (!!countyMaps && countyMaps.features.length > 0) {
      setDataBoundingBox(getBoundingBoxFeatureCollection(countyMaps));
    }
  }, [isLoading, projectContext.filters]);

  useEffect(() => {
    if (!history.location.search || !countyMaps || countyMaps.features.length == 0) return;

    const filteredProjectServiceAreaMaps = getFilteredProjectServiceAreaMapsByFilters;

    if (filteredProjectServiceAreaMaps.features.length === 0) {
      setDataBoundingBox(getBoundingBoxFeatureCollection(countyMaps));
    } else if (!!selectedCountyFeature) {
      setDataBoundingBox(getBoundingBoxFeature(selectedCountyFeature));
    } else {
      setDataBoundingBox(getBoundingBoxFeatureCollection(filteredProjectServiceAreaMaps));
    }
  }, [history.location.search])

  useEffect(() => {
    setIsLoading(true);
    setCountyMaps(null);
    projectService?.getProjectServiceAreaMapAsync(
      projectContext.project.projectId,
      projectContext.runConfiguration.projectRunConfigurationId,
      projectContext.runConfiguration.isProjected || projectContext.runConfiguration.isProjectedTermed,
      projectContext.runConfiguration.isTermed || projectContext.runConfiguration.isProjectedTermed
    ).then((serviceAreaMap) => {
      const serviceAreaMaps = convertToFeatureCollection(serviceAreaMap.filter(f => !!f));

      if (serviceAreaMaps.features.length === 0) return;

      setCountyMaps(serviceAreaMaps);

      setDataBoundingBox(getBoundingBoxFeatureCollection(serviceAreaMaps));

    }).finally(() => {
      setIsLoading(false);
    });
  }, [projectContext.runConfiguration]);

  const handleMouseMoveOnCounty = (e: MapMouseEvent) => {
    setMouseCoordinates(e.lngLat);
    setHoveredCountySSA(e.features[0].properties.countySSA);
  }

  const handleMouseLeaveOnCounty = () => {
    setHoveredCountySSA(null);
  }

  const handleMouseClickOnCounty = (e: MapMouseEvent) => {
    const feature = e?.features?.[0];

    if (!feature?.properties?.state || !feature?.properties?.countySSA) {
      return;
    }

    setStateFilter(feature.properties.state);
    setCountyFilter(feature.properties.countySSA);
  }

  const hoveredCountyMap = useMemo<Feature<Geometry, ProjectServiceAreaMap>>(() => {
    return countyMaps?.features?.find(map => map.properties.countySSA === hoveredCountySSA);
  }, [hoveredCountySSA])

  const contextValue: GapDensityMapDataContextProps = {
    countyMapFilters,
    countyMaps,
    dataBoundingBox,
    filters: projectContext.filters,
    getBoundingBoxFeature,
    getBoundingBoxFeatureCollection,
    handleMouseClickOnCounty,
    handleMouseLeaveOnCounty,
    handleMouseMoveOnCounty,
    hoveredCountyMap,
    isLoading,
    mapRef,
    mouseCoordinates,
    selectedCountyFeature,
    setCountyFilter,
  }

  return (
    <GapDensityMapDataContext value={contextValue}>
      {props.children}
    </GapDensityMapDataContext>
  )
}

export { GapDensityMapDataContext, GapDensityMapDataContextProvider, GapDensityMapDataContextProps };